<script>
	import { Link } from "svelte-routing";
	import { pageOffset, toggleUniversalToPageScroll, isPortraitScreen } from '../js/model/stores';
	import { animateElement } from '../js/utils/animate';

	let _nav;
	let scrollHeight;
	let portrait;

	export let animate = true;

	let animation = (value) => {
		animateElement(value, _nav, (currentPageOffset) => {
			_nav.style.transition = "opacity 1s";
			if (currentPageOffset > window.innerHeight * (portrait ? 1 : 1.5)) {
				_nav.style.opacity = 1;
				_nav.style.pointerEvents = 'auto';
			} else {
				_nav.style.opacity = 0;
				_nav.style.pointerEvents = 'none';
			}

			if (parseInt(currentPageOffset) > scrollHeight * 0.8) {
				_nav.classList.add('bottom_navigation--maximized');
			} else {
				_nav.classList.remove('bottom_navigation--maximized');
			}
		});
	};

	function setScrollHeight() {
		scrollHeight = Math.max(
			document.body.scrollHeight, document.documentElement.scrollHeight,
			document.body.offsetHeight, document.documentElement.offsetHeight,
			document.body.clientHeight, document.documentElement.clientHeight
		);
	}

	let prepareScene = () => {
		if (!animate) {
			return;
		}
		if (!_nav) {
			requestAnimationFrame(prepareScene);
			return;
		}

		_nav.style.opacity = 0;
		_nav.classList.remove('bottom_navigation--maximized');
		window.addEventListener('resize', setScrollHeight);
		setScrollHeight();
		pageOffset.subscribe(animation);
		isPortraitScreen.subscribe((state) => {
			portrait = state;
		});
	};

	prepareScene();
</script>

<style>
	.bottom_navigation {
		position: sticky;
		bottom: 0;
		left: 0;
		width: 100%;
		z-index: 30;
		overflow: hidden;
	}

	.wave {
		width: 101vw; /* or else Safari will show small gaps left and right */
		line-height: 0;
	}

	:global(.bottom_navigation svg) {
		width: 100%;
		height: auto;
	}

	.bottom_navigation__content {
		background-color: #94bbd8;
		display: block;
		position: relative;
		left: 0;
		bottom: 0;
		width: 100%;
		height: 5em;
		color: #FFF;
		font-weight: var(--font-h2-weight);
		transition: height .5s linear;
		margin-top: -1px;
	}

	.portrait .bottom_navigation__content {
		height: 4em;
	}

	.bottom_navigation--maximized.portrait .bottom_navigation__content {
		height: 6em;
	}

	@media (min-width: 640px) {
		.bottom_navigation__content {
			display: flex;
		}
	}

	.bottom_navigation__content span {
		top: 50%;
		position: absolute;
		left: 0;
		transform: translateY(-50%);
		width: 100%;
	}

	.initiatives {
		width: calc(100% - 4vh);
		display: flex;
		flex-wrap: wrap;
		height: calc(60% - 4vh);
		padding-top: 2vh;
		margin-left: 2.5vh;
	}

	.portrait .initiatives {
		height: 70%;
		padding-top: 1.5vh;
		padding-bottom: .5vh;
	}

	.bottom_navigation--maximized.portrait .bottom_navigation__content .initiatives {
		height: 45%;
	}

	@media (min-width: 640px) {
		.initiatives {
			width: 62.5%;
			height: auto;
			padding-top: unset;
			margin-left: unset;
		}
	}

	.initiatives__text {
		flex: 3;
		text-align: right;
		margin-right: 4vmin;
		position: relative;
		display: none;
	}

	@media (min-width: 640px) {
		.initiatives__text {
			display: block;
		}
	}

	.initiatives__associations {
		flex: 4;
		display: flex;
		flex-wrap: wrap;
		height: 85%;
	}

	.initiatives__associations a {
		flex: 1;
		width: 100%;
		margin: 0 0.5em;
	}

	.initiatives__associations a:first-of-type {
		flex: 4;
	}

	.association_image {
		height: 100%;
		background-repeat: no-repeat;
		background-size: contain;
		background-position: center;
	}

	.links {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 10px;
		height: calc(40% - 3vh);
	}

	.portrait .links {
		display: none;
		height: calc(40% - 2vh);
	}

	.bottom_navigation--maximized.portrait .bottom_navigation__content .links {
		display: flex;
	}

	@media (min-width: 640px) {
		.links {
			width: 30%;
			height: auto;
		}
	}

	.links__link {
		flex: 1;
		position: relative;
		color: #FFF;
	}

	:global(.links__link a) {
		color: #FFF;
	}

	.links__link--text-left {
		text-align: left;
	}

	.links__link--text-right {
		text-align: right;
	}

	.links__divider {
		position: relative;
		text-align: center;
		width: 1vw;
		margin: 0 10px;
	}

	.portrait .links__divider {
		margin: 0;
	}
</style>

<div class:portrait class="bottom_navigation bottom_navigation--maximized" bind:this={_nav}>
	<div class="wave">
		<svg width="1440" height="31" version="1.1" viewBox="0 0 1440 31" xmlns="http://www.w3.org/2000/svg">
			<path d="m0 111.9v-93.9c72-8.9 205.3-18 362.8-18 197 0 508 31 738 31 134 0 244.5-8.7 339.2-21.9v102.8z" fill="#fff"/>
			<path d="m0 31v-13c72-8.9 205.3-18 362.8-18 197 0 508 31 738 31 134 0 244.5-8.7001 339.2-21.9v21.9z" fill="#5a97c3" fill-opacity=".65"/>
		</svg>
	</div>
	<div class="bottom_navigation__content">
		<div class="initiatives">
			<div class="initiatives__text">
				<span>Eine Initiative von:</span>
			</div>
			<div class="initiatives__associations">
				<a href="https://industrieverein.org" target="_blank">
					<div class="association_image association_image--big" style="background-image: url('./images/industrieverein_logo.png')"></div>
				</a>
				<a href="https://kreatives-chemnitz.de" target="_blank">
					<div class="association_image" style="background-image: url('./images/kreatives_chemnitz_logo.png')"></div>
				</a>
				<a href="https://buntmacherinnen.de" target="_blank">
					<div class="association_image" style="background-image: url('./images/buntmacherinnen_logo.png')"></div>
				</a>
			</div>
		</div>
		<div class="links">
			<div class="links__link links__link--text-right">
				<Link to="/impressum">
					<span>Impressum</span>
				</Link>
			</div>
			<div class="links__divider">
				<span>|</span>
			</div>
			<div class="links__link links__link--text-left">
				<Link to="/datenschutz">
					<span>Datenschutz</span>
				</Link>
			</div>
		</div>
	</div>
</div>
